// In-cell control primitives for the logistics-calculation document.
//
// WHY raw `input` instead of `ui/Input`: the manager fills the document in the
// cell where the value is printed, so a control must be invisible until hover or
// focus and must inherit the paper's font, size and colour. The shared
// primitives hard-code a 32 px bordered control box with the app font in inline
// styles, which a feature stylesheet cannot undo and which would print as a form
// field.
//
// INVARIANT: every cell below is a mode switch, not a `disabled` control. View
// mode emits a text node and nothing else, so the customer's page and the
// printed sheet contain no form elements at all. Each pair is split into a
// wrapper that reads the mode and an inner component that owns the field hook,
// because a hook must not be called on only one of the two paths.
import { createContext, useContext, useState, type ReactNode } from 'react';
import { useController, useFormContext, useFormState, useWatch, type Path } from 'react-hook-form';
import type { LogisticsCalculationEditableContent } from '@magimp/shared';
import { isoToDateTimeInput } from '../components/CargoDateTimeField.js';

type Form = LogisticsCalculationEditableContent;
export type DocumentPath = Path<Form>;
export type DocumentOption = { value: string; label: string };

const DocumentEditingContext = createContext(false);

export function DocumentEditingProvider({
  editing,
  children,
}: {
  editing: boolean;
  children: ReactNode;
}) {
  return (
    <DocumentEditingContext.Provider value={editing}>{children}</DocumentEditingContext.Provider>
  );
}

export function useDocumentEditing(): boolean {
  return useContext(DocumentEditingContext);
}

export function text(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

/**
 * A cell's field plus whether it is what a failed save is complaining about.
 *
 * WHY the ref travels with it: react-hook-form focuses the first invalid field
 * that registered one, so a cell that forwards it is a cell the manager is
 * taken to instead of being left to hunt for it under a banner.
 */
export function useCell(name: DocumentPath) {
  const { control } = useFormContext<Form>();
  const { field, fieldState } = useController({ control, name });
  return { ...field, invalid: fieldState.invalid };
}

/**
 * Whether a failed save is complaining about one leaf of a cell.
 *
 * WHY not a second `useController`: registering a leaf writes its path into the
 * form values, which would materialise `{amount: undefined}` on every cost cell
 * the manager never touched — the one thing `DocumentCost` guarantees it does
 * not do. This reads the state a cell is in without joining it.
 */
export function useCellInvalid(name: DocumentPath): boolean {
  const { control, getFieldState } = useFormContext<Form>();
  const formState = useFormState({ control, name });
  return getFieldState(name, formState).invalid;
}

/** The dot the schema stores, from the comma the manager types. */
export function decimalPoint(value: string): string {
  return value.replace(',', '.');
}

/** The live form value of a cell, for the parts of a row that are not controls. */
export function useCellValue(name: DocumentPath): string {
  const { control } = useFormContext<Form>();
  return text(useWatch({ control, name }));
}

/**
 * A single-line cell: typed in place while editing, printed as text otherwise.
 *
 * WHY `numeric` is opt-in per cell: this control also serves the cargo name and
 * the cost captions, where a typed comma is punctuation the customer reads —
 * only a cell whose value is a number may have it swapped for a dot.
 *
 * An empty cell shows what belongs in it — its own label, or `0` for a number —
 * because the editing stylesheet draws the blank under a cell only while its
 * placeholder shows; a cell with none would vanish from the paper once empty.
 */
export function DocText({
  name,
  label,
  value,
  type = 'text',
  numeric = false,
  placeholder,
}: {
  name: DocumentPath;
  label: string;
  value: string;
  type?: 'text' | 'date';
  numeric?: boolean;
  placeholder?: string | undefined;
}) {
  const editing = useDocumentEditing();
  return editing ? (
    <TextControl
      name={name}
      label={label}
      type={type}
      numeric={numeric}
      placeholder={placeholder}
    />
  ) : (
    <>{value}</>
  );
}

function TextControl({
  name,
  label,
  type,
  numeric,
  placeholder,
}: {
  name: DocumentPath;
  label: string;
  type: 'text' | 'date';
  numeric: boolean;
  placeholder: string | undefined;
}) {
  const field = useCell(name);
  return (
    <input
      className="doc-input"
      type={type}
      ref={field.ref}
      aria-label={label}
      aria-invalid={field.invalid || undefined}
      {...(numeric ? { inputMode: 'decimal' as const } : {})}
      placeholder={placeholder ?? (numeric ? '0' : label)}
      value={text(field.value)}
      onChange={(event) =>
        field.onChange(numeric ? decimalPoint(event.target.value) : event.target.value)
      }
      onBlur={field.onBlur}
    />
  );
}

/**
 * A cell whose stored value is an instant but whose control is a calendar date.
 *
 * WHY noon UTC: the sheet prints only the day and month, so the manager picks a
 * date. Stored at 12:00Z, that date reads the same on every screen between
 * UTC-11 and UTC+11 — the customer never sees the day before the one typed.
 */
export function DocDate({
  name,
  label,
  value,
}: {
  name: DocumentPath;
  label: string;
  value: string;
}) {
  const editing = useDocumentEditing();
  return editing ? <DateControl name={name} label={label} /> : <>{value}</>;
}

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/u;

/** The stored instant as the local calendar date a `type="date"` control holds. */
export function isoToDateInput(value: string | undefined): string {
  if (!value) return '';
  if (DATE_ONLY.test(value)) return value;
  return isoToDateTimeInput(value).slice(0, 10);
}

/** A typed calendar date as the noon-UTC instant the schema stores. */
export function dateInputToIso(value: string): string | undefined {
  return DATE_ONLY.test(value) ? `${value}T12:00:00.000Z` : undefined;
}

function DateControl({ name, label }: { name: DocumentPath; label: string }) {
  const field = useCell(name);
  const [focused, setFocused] = useState(false);
  const value = isoToDateInput(text(field.value) || undefined);
  return (
    <span className="doc-date" data-empty={value === '' && !focused ? '' : undefined}>
      <input
        className="doc-input"
        type="date"
        ref={field.ref}
        aria-label={label}
        aria-invalid={field.invalid || undefined}
        value={value}
        // WHY the raw fallback: an incomplete date must stay in the box the
        // manager is typing into rather than snap back to empty.
        onChange={(event) =>
          field.onChange(
            event.target.value === ''
              ? undefined
              : (dateInputToIso(event.target.value) ?? event.target.value),
          )
        }
        onFocus={() => setFocused(true)}
        onBlur={() => {
          setFocused(false);
          field.onBlur();
        }}
      />
    </span>
  );
}

/**
 * A one-of-many condition printed as text and chosen through radios.
 *
 * INVARIANT: the control prints nothing. A condition the document states on
 * paper is printed by the cell that owns it — see `DocumentPayable`'s payment
 * terms — because not every choice the manager makes belongs on the sheet.
 */
export function DocRadios({
  name,
  label,
  value,
  options,
  onValueChange,
}: {
  name: DocumentPath;
  label: string;
  value: string;
  options: readonly DocumentOption[];
  onValueChange?: (value: string) => void;
}) {
  const editing = useDocumentEditing();
  return editing ? (
    <RadioControl name={name} label={label} options={options} onValueChange={onValueChange} />
  ) : (
    <>{value}</>
  );
}

function RadioControl({
  name,
  label,
  options,
  onValueChange,
}: {
  name: DocumentPath;
  label: string;
  options: readonly DocumentOption[];
  onValueChange: ((value: string) => void) | undefined;
}) {
  const field = useCell(name);
  return (
    <span className="doc-radios" role="radiogroup" aria-label={label} data-editing="">
      {options.map((option) => (
        <label key={option.value}>
          <input
            type="radio"
            name={field.name}
            value={option.value}
            checked={field.value === option.value}
            onChange={() => {
              field.onChange(option.value);
              onValueChange?.(option.value);
            }}
          />
          <span>{option.label}</span>
        </label>
      ))}
    </span>
  );
}

/** An edit-only affordance; `data-editing` is what the print rule hides. */
export function DocButton({
  label,
  onClick,
  danger,
}: {
  label: string;
  onClick: () => void;
  danger?: boolean;
}) {
  const editing = useDocumentEditing();
  if (!editing) return null;
  return (
    <button
      className="doc-action"
      type="button"
      data-editing=""
      data-danger={danger ? '' : undefined}
      onClick={onClick}
    >
      {label}
    </button>
  );
}
